import {
  AccountCircleIcon,
  DeleteIcon,
  EditIcon,
  InfoOutlineIcon,
  MoreVerticalOutlineIcon,
  PlusOutlineIcon,
  RestartIcon,
} from '@carboncopyco/icons';
import type { UserPersonas } from '@carboncopyco/types/desktop';
import {
  Avatar,
  Badge,
  Button,
  ColorHex,
  Dropdown,
  DropdownItem,
  InlineLink,
  Modal,
  Spacer,
  TextBase,
  TextCaption,
  TextLargeParagraphTitle,
  Tooltip,
  TooltipBubble,
} from '@carboncopyco/ui';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { getInitials } from '../../../util/util';
import { assignColor } from '../../shared/utils';
import ModalWrapper from '../Modals/ModalWrapper';
import { getPersonasIdDefaultOnTop } from './util';

interface ManagePersonasModalModalProps {
  open: boolean;
  onClose: () => void;
  onAddNewPersona: () => void;
  onEditPersona: (personaId: string) => void;
  onResetPersona: (personaId: string) => void;
  onDeletePersona: (personaId: string) => void;
  personasData: UserPersonas;
}

export const ManagePersonasModal = ({
  open,
  onClose,
  onAddNewPersona,
  onEditPersona,
  onResetPersona,
  onDeletePersona,
  personasData,
}: ManagePersonasModalModalProps) => {
  const setAsDefault = (personaId: string) => {
    window.dsDesktop?.user.setDefaultPersona(personaId);
  };
  const infoTooltipRef = useRef<HTMLDivElement>(null);
  const guestTooltipRef = useRef<HTMLDivElement>(null);

  const [selectedPersonaId, setSelectedPersonaId] = useState('');

  return (
    <div>
      <ModalWrapper
        isVisible
        data-testid="manage-personas-modal"
      >
        <Modal
          title="Manage profiles"
          open={open}
          onClose={onClose}
        >
          <div className="flex flex-col w-ds-md-modal">
            <div className="mt-5 mb-3 flex flex-col">
              <div className="flex">
                <TextBase
                  color="text-ds-natural-600"
                  label="Showcase multiple user roles easily."
                />
                <div
                  ref={infoTooltipRef}
                  className="ml-2"
                >
                  <InfoOutlineIcon
                    size={20}
                    color={ColorHex.natural200}
                  />
                </div>
                <div onClick={(event) => event.stopPropagation()}>
                  <Tooltip
                    interactive
                    shadow="md"
                    placement="bottom-start"
                    anchorRef={infoTooltipRef}
                    noArrow
                  >
                    <div className="w-ds-330 p-3 cursor-auto">
                      <TextBase
                        label="Just add profiles for each user role and login once when launching the presentation with your username and password. Then switch profiles before or during the demo."
                        color="text-ds-natural-600"
                      />
                    </div>
                  </Tooltip>
                </div>
              </div>

              <div className="pt-5">
                <TextCaption label="MY PROFILES" />
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {getPersonasIdDefaultOnTop(personasData).map((personaId) => (
                  <div
                    key={personaId}
                    className={classNames(
                      'px-4 py-5 mt-2 border border-ds-natural-150 rounded-md group'
                    )}
                  >
                    <div className="flex justify-between">
                      <div className="flex items-center justify-start">
                        <div>
                          {personasData.personas[personaId].isGuest ? (
                            <AccountCircleIcon
                              color="#9B9AA2"
                              size={48}
                            />
                          ) : (
                            <Avatar
                              initials={getInitials(
                                personasData.personas[personaId].name
                              )}
                              size="large"
                              backgroundColor={assignColor(
                                personasData.personas[personaId].name
                              )}
                            />
                          )}
                        </div>
                        <Spacer amount={1.25} />
                        <div>
                          <div className="w-ds-54 truncate flex items-center">
                            <TooltipBubble
                              placement="top"
                              title={personasData.personas[personaId].name}
                            >
                              <div className="w-ds-54 truncate">
                                <TextLargeParagraphTitle
                                  label={personasData.personas[personaId].name}
                                  color="text-ds-natural-600"
                                  ellipsis
                                />
                              </div>
                            </TooltipBubble>
                            <div className="pl-1">
                              {personasData.defaultId === personaId && (
                                <Badge
                                  label="DEFAULT"
                                  variant="grey"
                                />
                              )}
                            </div>
                            {personasData.personas[personaId].isGuest ? (
                              <>
                                <div
                                  ref={guestTooltipRef}
                                  className="ml-2"
                                >
                                  <InfoOutlineIcon
                                    size={20}
                                    color={ColorHex.natural200}
                                  />
                                </div>
                                <div
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <Tooltip
                                    interactive
                                    shadow="md"
                                    placement="bottom-start"
                                    anchorRef={guestTooltipRef}
                                    noArrow
                                  >
                                    <div className="p-3 cursor-auto">
                                      <TextBase
                                        label="This profile will not store your logins"
                                        color="text-ds-natural-600"
                                      />
                                    </div>
                                  </Tooltip>
                                </div>
                              </>
                            ) : null}
                          </div>
                          <div>
                            <TextCaption
                              label={
                                personasData.personas[personaId].description
                              }
                              color="text-ds-natural-300"
                              ellipsis
                            />
                          </div>
                        </div>
                      </div>
                      <div
                        className={`flex justify-center items-center group-hover:flex ${
                          selectedPersonaId === personaId ? '' : 'hidden'
                        }`}
                      >
                        <InlineLink
                          color="text-ds-primary-300"
                          label="Set as default"
                          onClick={() => setAsDefault(personaId)}
                        />
                        <div className="pl-5 pr-3">
                          <TooltipBubble
                            placement="top"
                            title="Edit persona"
                          >
                            <Button
                              dark
                              icon={<EditIcon />}
                              onClick={() => {
                                onEditPersona(personaId);
                              }}
                            />
                          </TooltipBubble>
                        </div>
                        <Dropdown
                          asChild
                          width={200}
                          onOpenChange={(isOpen) =>
                            setSelectedPersonaId(isOpen ? personaId : '')
                          }
                          triggerElement={
                            <Button
                              dark
                              small
                              icon={<MoreVerticalOutlineIcon />}
                            />
                          }
                        >
                          <DropdownItem
                            iconLeft={<RestartIcon />}
                            label="Reset"
                            onClick={() => {
                              onResetPersona(personaId);
                            }}
                          />
                          {personaId !== personasData.defaultId &&
                            !personasData.personas[personaId].isGuest && (
                              <DropdownItem
                                iconLeft={<DeleteIcon />}
                                label="Delete"
                                onClick={() => onDeletePersona(personaId)}
                              />
                            )}
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div
                onClick={onAddNewPersona}
                className={classNames(
                  'flex items-center justify-start px-2 py-5 mt-2 border border-ds-natural-150 border-dashed rounded-md hover:bg-slate-50 cursor-pointer'
                )}
              >
                <Button
                  link
                  data-testid="add-persona-button"
                  leftIcon={<PlusOutlineIcon />}
                  label="Add new profile"
                />
              </div>
            </div>
          </div>
        </Modal>
      </ModalWrapper>
    </div>
  );
};
